import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { BookOpen, CheckCircle, Target, Users, Heart, ArrowRight, Layers, PenTool, RotateCcw, HelpCircle, Sparkles, Lock, ShieldCheck } from 'lucide-react';

const QuranMethodSection = () => {
  const { language } = useLanguage();
  const sv = language === 'sv';

  const uniqueFeatures = [
    { icon: Layers, title: sv ? 'Steg-för-steg system' : 'Step-by-step system', desc: sv ? 'Eleven går igenom tydliga nivåer — från bokstäver till flytande läsning.' : 'Students progress through clear levels — from letters to fluent reading.' },
    { icon: PenTool, title: sv ? 'Praktiska övningar i varje lektion' : 'Practical exercises in every lesson', desc: sv ? 'Varje del innehåller övningar, repetition och kontrollfrågor.' : 'Each part includes exercises, repetition and review questions.' },
    { icon: Sparkles, title: sv ? 'Fokus på förståelse — inte bara memorering' : 'Focus on understanding — not just memorization', desc: sv ? 'Vi säkerställer att eleven verkligen förstår hur läsningen fungerar.' : 'We ensure the student truly understands how reading works.' },
    { icon: BookOpen, title: sv ? 'Tajweed från början' : 'Tajweed from the start', desc: sv ? 'Redan från start lär sig eleven korrekt uttal.' : 'From the very beginning, the student learns correct pronunciation.' },
    { icon: CheckCircle, title: sv ? 'Riktiga ord från Koranen tidigt' : 'Real Quranic words early on', desc: sv ? 'Eleven börjar läsa riktiga ord i ett tidigt skede.' : 'Students start reading real words at an early stage.' },
  ];

  const levels = sv
    ? ['Bokstäver och uttal', 'Harakat (korta vokaler)', 'Tanween och sukoon', 'Madd (förlängning)', 'Leen (mjuka ljud)', 'Läsning av ord från Koranen', 'Grundläggande tajweed-regler']
    : ['Letters and pronunciation', 'Harakat (short vowels)', 'Tanween and sukoon', 'Madd (elongation)', 'Leen (soft sounds)', 'Reading words from the Quran', 'Basic tajweed rules'];

  const lessonParts = sv
    ? ['Förklaring', 'Övningar', 'Repetition', 'Testfrågor']
    : ['Explanation', 'Exercises', 'Repetition', 'Test questions'];

  const lessonIcons = [BookOpen, PenTool, RotateCcw, HelpCircle];

  const goals = sv
    ? ['Läsa Koranen korrekt', 'Förstå grunderna i tajweed', 'Få självförtroende i sin läsning', 'Gå från nybörjare till självständig läsare']
    : ['Read the Quran correctly', 'Understand the basics of tajweed', 'Gain confidence in reading', 'Go from beginner to independent reader'];

  const audiences = sv
    ? ['Barn som börjar från grunden', 'Vuxna nybörjare', 'Elever som vill förbättra sin läsning', 'Alla som vill lära sig Koranen på ett strukturerat sätt']
    : ['Children starting from scratch', 'Adult beginners', 'Students who want to improve their reading', 'Anyone who wants to learn the Quran in a structured way'];

  return (
    <section className="py-24 relative overflow-hidden" data-testid="quran-method-section">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,61,46,0.04) 0%, transparent 40%, rgba(206,150,8,0.03) 100%)' }} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-6 tracking-wider"
            style={{ backgroundColor: 'rgba(11,61,46,0.08)', color: '#0B3D2E', border: '1px solid rgba(11,61,46,0.15)' }}>
            <BookOpen size={14} />
            QURAN ONLINE METHOD
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'En unik och strukturerad väg' : 'A unique and structured path'}
            <br />
            <span className="text-gold">{sv ? 'till att läsa Koranen' : 'to reading the Quran'}</span>
          </h2>
          <p className="text-base max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            {sv
              ? 'På Quran Online Sverige har vi utvecklat en egen metod för att lära ut Koranen från grunden — steg för steg, på ett tydligt och pedagogiskt sätt.'
              : 'At Quran Online Sverige we have developed our own method for teaching the Quran from the ground up — step by step, in a clear and pedagogical way.'}
          </p>
          <p className="text-sm mt-4 max-w-3xl mx-auto" style={{ color: 'var(--text-muted)' }}>
            {sv
              ? 'Vår metod är inspirerad av klassiska lärmetoder, men anpassad för dagens elever med en modern, strukturerad och effektiv inlärning.'
              : 'Our method is inspired by classical teaching methods, but adapted for today\'s students with modern, structured and effective learning.'}
          </p>
        </div>

        {/* Unique features */}
        <div className="mb-20">
          <h3 className="text-xl font-bold mb-8 text-center" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Vad gör vår metod unik?' : 'What makes our method unique?'}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {uniqueFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="p-6 rounded-2xl transition-all duration-300 hover:translate-y-[-2px]"
                  style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                  data-testid={`method-feature-${i}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'rgba(11,61,46,0.08)', border: '1px solid rgba(11,61,46,0.12)' }}>
                    <Icon size={20} style={{ color: '#0B3D2E' }} />
                  </div>
                  <h4 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{f.title}</h4>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Exclusive access */}
        <div className="p-6 rounded-2xl mb-20 max-w-4xl mx-auto" style={{ background: 'linear-gradient(135deg, rgba(11,61,46,0.06) 0%, rgba(11,61,46,0.02) 100%)', border: '2px solid rgba(11,61,46,0.15)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Lock size={22} style={{ color: '#0B3D2E' }} />
            <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Exklusiv åtkomst' : 'Exclusive access'}
            </h3>
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            {sv ? 'Denna bok är endast tillgänglig via vår plattform:' : 'This book is only available via our platform:'}
          </p>
          <div className="space-y-2">
            {(sv
              ? ['Endast för registrerade elever', 'Åtkomst kräver personlig kod från lärare', 'Upphovsrättsskyddad \u2014 kan inte kopieras eller laddas ner']
              : ['Only for registered students', 'Access requires personal code from teacher', 'Copyright protected \u2014 cannot be copied or downloaded']
            ).map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <ShieldCheck size={16} style={{ color: '#0B3D2E' }} className="flex-shrink-0 mt-0.5" />
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* How teaching works */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {/* Levels */}
          <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <h3 className="text-lg font-bold mb-5" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Så fungerar undervisningen' : 'How teaching works'}
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
              {sv ? 'Vår metod är uppbyggd i nivåer:' : 'Our method is built in levels:'}
            </p>
            <div className="space-y-3">
              {levels.map((level, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: 'var(--gold)', color: '#fff' }}>{i + 1}</div>
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{level}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Lesson structure */}
          <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <h3 className="text-lg font-bold mb-5" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Varje lektion innehåller' : 'Each lesson contains'}
            </h3>
            <div className="space-y-4">
              {lessonParts.map((part, i) => {
                const Icon = lessonIcons[i];
                return (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-xl"
                    style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                      <Icon size={18} className="text-gold" />
                    </div>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{part}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Goals + Audience */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Goals */}
          <div className="p-6 sm:p-8 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(206,150,8,0.08) 0%, rgba(206,150,8,0.02) 100%)', border: '2px solid rgba(206,150,8,0.2)' }}>
            <div className="flex items-center gap-3 mb-5">
              <Target size={22} className="text-gold" />
              <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Målet med Quran Online Method' : 'The goal of Quran Online Method'}
              </h3>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
              {sv ? 'Att hjälpa eleven:' : 'To help the student:'}
            </p>
            <div className="space-y-3">
              {goals.map((g, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Sparkles size={16} className="text-gold flex-shrink-0 mt-0.5" />
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{g}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Audience */}
          <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-center gap-3 mb-5">
              <Users size={22} style={{ color: '#0B3D2E' }} />
              <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'För vem passar denna metod?' : 'Who is this method for?'}
              </h3>
            </div>
            <div className="space-y-3">
              {audiences.map((a, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle size={16} style={{ color: '#0B3D2E' }} className="flex-shrink-0 mt-0.5" />
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{a}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vision */}
        <div className="p-6 sm:p-8 rounded-2xl text-center mb-12" style={{ background: 'linear-gradient(135deg, rgba(11,61,46,0.06) 0%, rgba(11,61,46,0.02) 100%)', border: '2px solid rgba(11,61,46,0.15)' }}>
          <Heart size={28} style={{ color: '#0B3D2E' }} className="mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Vår vision' : 'Our vision'}
          </h3>
          <p className="text-base max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            {sv
              ? 'Vi vill göra det enkelt, tydligt och inspirerande att lära sig Koranen — oavsett ålder eller nivå.'
              : 'We want to make it simple, clear and inspiring to learn the Quran — regardless of age or level.'}
          </p>
        </div>

        {/* CTA */}
        <div className="p-8 rounded-2xl text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '2px solid var(--gold)' }}>
          <p className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Quran Online Method' : 'Quran Online Method'}
          </p>
          <p className="text-sm mb-6 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            {sv
              ? 'Vår egen strukturerade metod för att lära sig läsa Koranen från grunden. Med steg-för-steg lektioner, övningar och tydlig vägledning hjälper vi elever att läsa korrekt med tajweed från dag ett.'
              : 'Our own structured method for learning to read the Quran from scratch. With step-by-step lessons, exercises and clear guidance, we help students read correctly with tajweed from day one.'}
          </p>
          <Link to="/noorania-bok" className="btn-gold inline-flex items-center gap-2" data-testid="quran-method-cta">
            {sv ? 'Öppna Quran Online Method' : 'Open Quran Online Method'} <ArrowRight size={18} />
          </Link>
        </div>

      </div>
    </section>
  );
};

export default QuranMethodSection;
